import React, { useState } from 'react';
import {
  Container, Box, Paper, Button, Typography, Avatar, FormControl,
  InputLabel, Select, MenuItem, FormControlLabel, Switch, Tab, Tabs,
  TextField, Grid, Chip, IconButton, Divider, useTheme, useMediaQuery, FormHelperText
} from '@mui/material';
import { styled } from '@mui/material/styles';
import PersonIcon from '@mui/icons-material/Person';
import EditIcon from '@mui/icons-material/Edit';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import SaveIcon from '@mui/icons-material/Save';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PaletteIcon from '@mui/icons-material/Palette';
import TranslateIcon from '@mui/icons-material/Translate';
import Footer from '../Footer/Footer';
import { useLocation } from 'react-router-dom';
import { useUserProfile } from './UserProfileContext';

export interface UserProfile {
  ethnicity: string;
  religion: string;
  location: string;
  politicalView: string;
  topic: string;
  verified: boolean;
  veteran: boolean;
  userName: string;
  userHandle: string;
}

interface UserProfileSettingsProps {
  userProfile: UserProfile;
  setUserProfile: React.Dispatch<React.SetStateAction<UserProfile>>;
}


const UploadButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  bottom: '-30px',
  left: '50%',
  marginLeft: '30px',
  backgroundColor: theme.palette.primary.main,
  color: 'white',
  '&:hover': {
    backgroundColor: theme.palette.primary.dark,
  }
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  position: 'relative',
  display: 'inline-block',
  marginBottom: theme.spacing(1),
  '&:after': {
    content: '""',
    position: 'absolute',
    bottom: '-5px',
    left: 0,
    width: '40px',
    height: '3px',
    backgroundColor: theme.palette.primary.main,
  }
}));

const FormSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1),
  marginBottom: theme.spacing(0),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: '#fff',
  //boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
  /*transition: 'box-shadow 0.3s ease',
  '&:hover': {
    boxShadow: '0 5px 15px rgba(0,0,0,0.1)',
  },*/
}));

const StyledTab = styled(Tab)(({ theme }) => ({
  textTransform: 'none',
  minWidth: 0,
  fontWeight: 500,
  fontSize: '0.9rem',
  marginRight: theme.spacing(3),
  '&.Mui-selected': {
    color: theme.palette.primary.main,
    fontWeight: 600,
  },
}));

export const validateProfile = (profile: UserProfile): boolean => {
  // Check if any required fields are empty or default values
  return Boolean(
    profile.ethnicity && 
    profile.religion && 
    profile.location && 
    profile.politicalView &&
    profile.ethnicity !== 'select' &&
    profile.religion !== 'select' &&
    profile.location !== 'select' &&
    profile.politicalView !== 'select'
  );
};

const UserProfileSettings = () => {
  const location = useLocation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [tabValue, setTabValue] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  
  // Use the context instead of props
  const { userProfile, updateUserProfile } = useUserProfile();

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };
  
  const handleSave = () => {
    // Validate profile before saving
    if (!validateProfile(userProfile)) {
      // Show error toast or alert
      alert("Please fill in all profile fields before saving");
      return;
    }
    
    setIsSaving(true);
    // Simulate saving to backend
    setTimeout(() => {
      setIsSaving(false);
    }, 1000);
  };

  const fixDuplicateLocationOptions = [
    { value: "Northeast", label: "Northeast" },
    //{ value: "Southeast", label: "Southeast" },
    //{ value: "Northwest", label: "Northwest" },
    //{ value: "Midatlantic", label: "Midatlantic" },
    { value: "Midwest", label: "Midwest" },
    //{ value: "Southwest", label: "Southwest" },
    { value: "South", label: "South" },
    { value: "West", label: "West" },
    //{ value: "Alaska", label: "Alaska" },
    //{ value: "Texas", label: "Texas" },
    //{ value: "California", label: "California" },
    //{ value: "Florida", label: "Florida" },
    //{ value: "Rural", label: "Rural" },
    //{ value: "Urban", label: "Urban" },
    //{ value: "Suburban", label: "Suburban" }
  ];

  return (
    <Container maxWidth="md" sx={{ py: 0 }}>
      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          overflow: 'hidden',
          mb: 4
        }}
      >
        {/* Profile Header with Cover Image and Avatar */}
        {<Box
              sx={{
                    position: 'relative',
                    height: '30px',
                    backgroundColor: '#002a5a',
                    borderRadius: '10px 10px 0 0',
                    overflow: 'visible',
                    marginBottom: '10px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}
                >
                {/*<Avatar 
                    src="/Star.svg" 
                    alt={userProfile.userName}
                    sx={{
                    width: 120,
                    height: 120,
                    //border: '4px solid white',
                    position: 'absolute',
                    top: '50%', // Changed from bottom: 0
                    left: '50%',
                    transform: 'translate(-50%, -50%)', // Added Y-axis translation
                    //boxShadow: 3,
                    zIndex: 2,
                    }}
                />
            <IconButton 
                size="small"
                sx={{
                position: 'absolute',
                bottom: '-30px',
                left: '50%',
                marginLeft: '30px',
                backgroundColor: 'primary.main',
                color: 'white',
                '&:hover': {
                    backgroundColor: 'primary.dark',
                }
                }}
            >
                <PhotoCameraIcon fontSize="small" />
            </IconButton>*/}
            </Box> }

        {/* Profile Name and Badges Section */}
        <Box sx={{ textAlign: 'center', mt: 3, px: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            <Typography variant="h4" component="h1" fontWeight="500">
            {userProfile.userName}
            </Typography>
            {/*<IconButton size="small"><EditIcon fontSize="small" /></IconButton>*/}
        </Box>
          <Typography variant="subtitle1" color="text.secondary" gutterBottom>
            {userProfile.userHandle}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 1, mb: 0 }}>
            {userProfile.verified && (
              <Chip 
                label="Verified" 
                size="small" 
                color="primary" 
                sx={{ borderRadius: 1 }}
              />
            )}
            {userProfile.veteran && (
              <Chip 
                label="Veteran" 
                size="small" 
                color="secondary" 
                sx={{ borderRadius: 1 }}
              />
            )}
          </Box>
        </Box>
        
        {/* Profile Tabs (We need this for later) */}
        {/*<Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="profile tabs"
            variant={isSmallScreen ? "scrollable" : "standard"}
            scrollButtons={isSmallScreen ? "auto" : undefined}
            allowScrollButtonsMobile
            sx={{ minHeight: 48 }}
          >
            <StyledTab label="Personal Details" icon={<PersonIcon />} iconPosition="start" />
            <StyledTab label="Verification & Status" icon={<PaletteIcon />} iconPosition="start" />
          </Tabs>
        </Box>*/}
        {/* Help text */}
      <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
        These settings determine how you'll be matched with other users. For the best experience, please ensure that fields are accurate.
      </Typography>

        {/* Tab Panels */}
        <Box sx={{ p: 3 }}>
          {/* Personal Details Tab */}
          {tabValue === 0 && (
            <Grid container spacing={0}>
              <Grid item xs={12}>
                <FormSection>
                  <SectionTitle variant="h6">Personal Information</SectionTitle>
                  <Grid container spacing={2} sx={{ mt: 0 }}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Username"
                            variant="outlined"
                            inputProps={{ maxLength: 20 }}
                            value={userProfile.userName}
                            onChange={(e) => {
                            // Allow typing beyond limit but show error
                            updateUserProfile({ userName: e.target.value });
                            }}
                            error={userProfile.userName?.length > 19}
                            helperText={userProfile.userName?.length > 19 
                            
                            ? "Name must be 20 characters or less" 
                            : " "}  // Space preserves layout consistency
                        />
                        </Grid>
                        {/*<Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Your Handle"
                            variant="outlined"
                            disabled
                            inputProps={{ maxLength: 30 }}
                            value={userProfile.userHandle}
                            onChange={(e) => {
                            // Allow typing beyond limit but show error
                            updateUserProfile({ userHandle: e.target.value });
                            }}
                            error={userProfile.userHandle?.length > 29}
                            helperText={userProfile.userHandle?.length > 29 
                            ? "Handle must be 30 characters or less" 
                            : " "}  // Space preserves layout consistency
                            placeholder="@username"
                        />
                        </Grid>*/}
                  </Grid>
                </FormSection>
              </Grid>

              {/* Location & Viewpoint Section */}
              <Grid item xs={12} sx={{mb: 3}}>
                <FormSection>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <LocationOnIcon color="primary" />
                    <SectionTitle variant="h6">Location & Viewpoint</SectionTitle>
                  </Box>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth error={!userProfile.location || userProfile.location === 'select'}>
                        <InputLabel>Location</InputLabel>
                        <Select
                          value={userProfile.location || 'select'}
                          label="Location"
                          onChange={(e) => updateUserProfile({ location: e.target.value })}
                        >
                          <MenuItem value="select" disabled>Select...</MenuItem>
                          {fixDuplicateLocationOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                        {(!userProfile.location || userProfile.location === 'select') && 
                          <FormHelperText>Required for matching with others</FormHelperText>}
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth error={!userProfile.politicalView || userProfile.politicalView === 'select'}>
                        <InputLabel>Political View</InputLabel>
                        <Select
                          value={userProfile.politicalView || 'select'}
                          label="Political View"
                          onChange={(e) => updateUserProfile({ politicalView: e.target.value })}
                        >
                          <MenuItem value="select" disabled>Select...</MenuItem>
                          <MenuItem value="Republican">Republican</MenuItem>
                          <MenuItem value="Democrat">Democrat</MenuItem>
                          <MenuItem value="Independent">Independent</MenuItem>
                          <MenuItem value="Green">Green</MenuItem>
                          <MenuItem value="Libertarian">Libertarian</MenuItem>
                        </Select>
                        {(!userProfile.politicalView || userProfile.politicalView === 'select') && 
                          <FormHelperText>Required for matching with others</FormHelperText>}
                      </FormControl>
                    </Grid>
                  </Grid>
                </FormSection>
              </Grid>
                    {/* Demographics Section */}
              <Grid item xs={12}>
                <FormSection>
                  <SectionTitle variant="h6">Demographics</SectionTitle>
                  <Grid container spacing={1} sx={{ mt: 1 }}>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth error={!userProfile.ethnicity || userProfile.ethnicity === 'select'}>
                        <InputLabel>Ethnicity</InputLabel>
                        <Select
                          value={userProfile.ethnicity || 'select'}
                          label="Ethnicity"
                          onChange={(e) => updateUserProfile({ ethnicity: e.target.value })}
                        >
                          <MenuItem value="select" disabled>Select...</MenuItem>
                          <MenuItem value="White">White</MenuItem>
                          <MenuItem value="Black">Black</MenuItem>
                          <MenuItem value="Hispanic">Hispanic</MenuItem>
                          <MenuItem value="Middle Eastern">Middle Eastern</MenuItem>
                          <MenuItem value="Asian">Asian</MenuItem>
                          <MenuItem value="Native American">Native American</MenuItem>
                          <MenuItem value="Pacific Islander">Pacific Islander</MenuItem>
                          <MenuItem value="Other">Other</MenuItem>
                        </Select>
                        {(!userProfile.ethnicity || userProfile.ethnicity === 'select') && 
                          <FormHelperText>Required for matching with others</FormHelperText>}
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth error={!userProfile.religion || userProfile.religion === 'select'}>
                        <InputLabel>Religion</InputLabel>
                        <Select
                          value={userProfile.religion || 'select'}
                          label="Religion"
                          onChange={(e) => updateUserProfile({ religion: e.target.value })}
                        >
                          <MenuItem value="select" disabled>Select...</MenuItem>
                          <MenuItem value="Christian">Christian</MenuItem>
                          <MenuItem value="Muslim">Muslim</MenuItem>
                          <MenuItem value="Jewish">Jewish</MenuItem>
                          <MenuItem value="Hindu">Hindu</MenuItem>
                          <MenuItem value="Buddhist">Buddhist</MenuItem>
                          <MenuItem value="Atheist">Atheist</MenuItem>
                          <MenuItem value="Agnostic">Agnostic</MenuItem>
                          <MenuItem value="Other">Other</MenuItem>
                        </Select>
                        {(!userProfile.religion || userProfile.religion === 'select') && 
                          <FormHelperText>Required for matching with others</FormHelperText>}
                      </FormControl>
                    </Grid>
                  </Grid>
                </FormSection>
              </Grid>
            </Grid>
          )}

          {/* Preferences Tab */}
          {tabValue === 1 && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <FormSection>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <TranslateIcon color="primary" />
                    <SectionTitle variant="h6">Verification</SectionTitle>
                  </Box>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <FormControlLabel
                        control={
                          <Switch 
                            checked={userProfile.verified}
                            onChange={(e) => updateUserProfile({ verified: e.target.checked })}
                            color="primary"
                          />
                        }
                        label="Verified User"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControlLabel
                        control={
                          <Switch 
                            checked={userProfile.veteran}
                            onChange={(e) => updateUserProfile({ veteran: e.target.checked })}
                            color="primary"
                          />
                        }
                        label="Veteran"
                      />
                    </Grid>
                  </Grid>
                </FormSection>
              </Grid>
            </Grid>
          )}
        </Box>
      </Paper>
      
      {/* Save Button */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
        {/*<Button 
          variant="contained" 
          size="large" 
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={isSaving}
          sx={{ 
            px: 4, 
            borderRadius: 2,
            boxShadow: 2,
            '&:hover': {
              boxShadow: 4
            }
          }}
        >
          {isSaving ? 'Saving...' : 'Save Changes'}
        </Button>*/}
      </Box>
      
      
      
      {location.pathname !== '/' && <Footer/>}
    </Container>
  );
}

export default UserProfileSettings;